import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { LoginResponse } from '../api/auth'
import {
  EXCEPTION_KIND_LABELS,
  PRIORITY_LABELS,
  backofficeQueueApi,
  type ContractExceptionsResponse,
  type ExceptionReview,
  type ExceptionReviewFilter,
  type ContractSlaResponse,
  type ExceptionKind,
  type PriorityCondition,
  type PriorityRule,
  type PriorityRuleInput,
  type PriorityRulePreview,
  type PriorityRulesResponse,
  type PriorityValue,
  type SlaPoliciesResponse,
  type SlaPolicy,
  type SlaPolicyPreview,
} from '../api/backofficeQueue'
import {
  LEAD_EXCEPTION_KIND_LABELS,
  leadExceptionsApi,
  type LeadExceptionKind,
  type LeadExceptionsResponse,
  type LeadSourceNormalizationPreview,
} from '../api/leadExceptions'
import { CloneConfigurationButton, useOpenAfterClone } from '../components/CloneConfigurationButton'
import {
  Button, Callout, Card, CardContent, CardHeader, DataTable, DataTableEmptyRow, Dialog, FilterBar,
  FormGrid, LoadingState, Page, PageHeader, Select, StatusPill, Switch, Tabs, TextField, type PillTone, useConfirm,
  usePrompt,
} from '../components/ui'
import { RuleLifecyclePanel } from '../components/RuleLifecyclePanel'

/** F-100: avviso dopo una clonazione (la copia nasce in bozza e va attivata a mano). */
const CLONE_NOTICE = 'Copia creata in bozza: è inattiva, attivala quando è pronta.'

/**
 * F-089 (TASK-0343, REQ-216-022 / REQ-215-001 / REQ-301-011 / REQ-051-001..002 / REQ-050-001..002 /
 * REQ-217-001): la coda del backoffice. Tre schede:
 *  - "Eccezioni": per causa (quante pratiche) e poi l'elenco filtrabile, con il link alla pratica;
 *  - "SLA": per stato, con "superato di Xh Ym" o "nei tempi". Le soglie sono fisse nel sistema (48 h, 4 h per
 *    le pratiche appena inviate); i valori 4 h / 8 h della specifica sono solo un riferimento, non applicati;
 *  - "Soglie SLA" (F-094, REQ-051-001): per stato, la soglia in vigore e da dove viene; tutti la vedono, la
 *    modifica è degli amministratori (Bozza -> anteprima -> Attiva con un motivo);
 *  - "Regole di priorità" (solo amministratori): Bozza -> anteprima (non scrive) -> Attiva.
 * Nessun importo. Il server decide chi vede cosa (403 per chi non è backoffice).
 */

interface Props {
  auth: LoginResponse
}

type Tab = 'exceptions' | 'leadExceptions' | 'sla' | 'thresholds' | 'rules'

function isForbidden(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : ''
  return msg.includes('403') || msg.toLowerCase().includes('forbidden') || msg.toLowerCase().includes('autorizzat')
}

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : 'Errore'
}

function fmtDateTime(s: string | null | undefined): string {
  return s ? new Date(s).toLocaleString('it-IT') : '—'
}

function fmtHours(n: number): string {
  return n.toLocaleString('it-IT', { maximumFractionDigits: 2 })
}

function fmtCount(n: number): string {
  return n.toLocaleString('it-IT')
}

const KIND_OPTIONS = Object.keys(EXCEPTION_KIND_LABELS) as ExceptionKind[]

export function BackofficeQueuePage({ auth }: Props) {
  const isAdmin = auth.role === 'Administrator' || auth.role === 'EqualAdmin'
  const [tab, setTab] = useState<Tab>('exceptions')

  const tabItems = [
    { id: 'exceptions', label: 'Eccezioni' },
    { id: 'leadExceptions', label: 'Eccezioni lead' },
    { id: 'sla', label: 'SLA' },
    { id: 'thresholds', label: 'Soglie SLA' },
    ...(isAdmin ? [{ id: 'rules', label: 'Regole di priorità' }] : []),
  ]

  return (
    <Page>
      <PageHeader
        title="Coda backoffice"
        icon="playlist_add_check"
        back={{ to: '/backoffice', label: 'Cruscotto backoffice' }}
        tabs={
          <Tabs
            aria-label="Sezioni della coda backoffice"
            items={tabItems}
            value={tab}
            onChange={id => setTab(id as Tab)}
          />
        }
      />

      {tab === 'exceptions' && <ExceptionsTab />}
      {tab === 'leadExceptions' && <LeadExceptionsTab isAdmin={isAdmin} />}
      {tab === 'sla' && <SlaTab />}
      {tab === 'thresholds' && <SlaThresholdsTab isAdmin={isAdmin} />}
      {tab === 'rules' && isAdmin && <RulesTab />}
    </Page>
  )
}

/* ── F-105: presa in carico e "ignora" (comuni alle schede delle eccezioni) ─────────────── */

const REVIEW_FILTER_OPTIONS: { value: ExceptionReviewFilter | ''; label: string }[] = [
  { value: '', label: 'Da lavorare (aperte e prese in carico)' },
  { value: 'open', label: 'Solo aperte' },
  { value: 'taken', label: 'Prese in carico' },
  { value: 'ignored', label: 'Ignorate' },
  { value: 'all', label: 'Tutte' },
]

function ReviewBadge({ review }: { review: ExceptionReview | null | undefined }) {
  if (!review) return <StatusPill tone="neutral">Aperta</StatusPill>
  const taken = review.decision === 'PresaInCarico'
  return (
    <StatusPill
      tone={taken ? 'info' : 'neutral'}
      title={`${fmtDateTime(review.decidedAt)}${review.note ? ` — ${review.note}` : ''}`}
    >
      {taken ? 'Presa in carico' : 'Ignorata'}
    </StatusPill>
  )
}

/** Finestra con la nota (obbligatoria) per ignorare un'eccezione. */
function IgnoreNoteDialog({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: (note: string) => void }) {
  const [note, setNote] = useState('')
  const trimmed = note.trim()
  return (
    <Dialog
      open
      onClose={onCancel}
      title="Ignora l'eccezione"
      as="form"
      onSubmit={e => { e.preventDefault(); onConfirm(trimmed) }}
      actions={
        <>
          <Button type="button" variant="text" onClick={onCancel}>Annulla</Button>
          <Button type="submit" variant="text" disabled={!trimmed}>
            Ignora
          </Button>
        </>
      }
    >
      <FormGrid columns={1}>
        <TextField
          id="exc-ignore-note"
          label="Motivo (obbligatorio)"
          multiline
          rows={3}
          maxLength={500}
          value={note}
          onChange={e => setNote(e.target.value)}
          supportingText="L'eccezione esce dall'elenco predefinito. Se la scheda viene modificata, ricompare da sola."
        />
      </FormGrid>
    </Dialog>
  )
}

/** Prendi in carico / Ignora / Riapri per una riga. */
function ReviewActions({
  review, busy, onTake, onIgnore, onReopen,
}: {
  review: ExceptionReview | null | undefined
  busy: boolean
  onTake: () => void
  onIgnore: (note: string) => void
  onReopen: () => void
}) {
  const [asking, setAsking] = useState(false)
  return (
    <div className="d-flex gap-1 justify-content-end flex-wrap">
      {!review && (
        <>
          <Button type="button" variant="outlined" size="sm" disabled={busy} onClick={onTake}>Prendi in carico</Button>
          <Button type="button" variant="outlined" size="sm" disabled={busy} onClick={() => setAsking(true)}>Ignora</Button>
        </>
      )}
      {review?.decision === 'PresaInCarico' && (
        <>
          <Button type="button" variant="outlined" size="sm" disabled={busy} onClick={() => setAsking(true)}>Ignora</Button>
          <Button type="button" variant="outlined" size="sm" disabled={busy} onClick={onReopen}>Riapri</Button>
        </>
      )}
      {review?.decision === 'Ignorata' && (
        <Button type="button" variant="outlined" size="sm" disabled={busy} onClick={onReopen}>Riapri</Button>
      )}
      {asking && (
        <IgnoreNoteDialog
          onCancel={() => setAsking(false)}
          onConfirm={note => { setAsking(false); onIgnore(note) }}
        />
      )}
    </div>
  )
}

/* ── F-105: eccezioni dei lead ──────────────────────────────────────────────────────────── */

const LEAD_KIND_OPTIONS = Object.keys(LEAD_EXCEPTION_KIND_LABELS) as LeadExceptionKind[]

function LeadExceptionsTab({ isAdmin }: { isAdmin: boolean }) {
  const [data, setData] = useState<LeadExceptionsResponse | null>(null)
  const [type, setType] = useState('')
  const [reviewFilter, setReviewFilter] = useState<ExceptionReviewFilter | ''>('')
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)
  const [normalizing, setNormalizing] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await leadExceptionsApi.list({
        ...(type ? { type } : {}),
        ...(reviewFilter ? { review: reviewFilter } : {}),
        limit: 200,
      }))
    } catch (e) {
      if (isForbidden(e)) setForbidden(true)
      else setError(errorText(e))
    } finally {
      setLoading(false)
    }
  }, [type, reviewFilter])

  useEffect(() => { void load() }, [load])

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await load()
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  if (forbidden) return <Callout tone="warning" role="alert">Non sei autorizzato a vedere la coda del backoffice.</Callout>

  return (
    <Page>
      {error && <Callout tone="error">{error}</Callout>}

      <FilterBar
        actions={isAdmin ? (
          <Button type="button" variant="outlined" size="sm" onClick={() => setNormalizing(true)}>
            Normalizza fonti
          </Button>
        ) : undefined}
      >
        <Select id="queue-lead-exc-type" label="Tipo" value={type} onChange={e => setType(e.target.value)}>
          <option value="">Tutti i tipi</option>
          {LEAD_KIND_OPTIONS.map(k => <option key={k} value={k}>{LEAD_EXCEPTION_KIND_LABELS[k]}</option>)}
        </Select>
        <Select
          id="queue-lead-exc-review"
          label="Presa in carico"
          value={reviewFilter}
          onChange={e => setReviewFilter(e.target.value as ExceptionReviewFilter | '')}
        >
          {REVIEW_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
      </FilterBar>

      {loading && !data && (
        <LoadingState label="Caricamento..." />
      )}

      {data && (
        <>
          <Card variant="outlined" padding="none">
            <CardHeader title="Per causa" />
            <DataTable variant="plain">
                <thead>
                  <tr><th>Causa</th><th>Tipo</th><th className="text-end">Lead</th><th /></tr>
                </thead>
                <tbody>
                  {data.aggregate.length === 0 && (
                    <DataTableEmptyRow colSpan={4} title="Nessuna eccezione sui lead." />
                  )}
                  {data.aggregate.map(row => (
                    <tr key={`${row.kind}-${row.label}`}>
                      <td>{row.label}</td>
                      <td>{LEAD_EXCEPTION_KIND_LABELS[row.kind]}</td>
                      <td className="text-end md-num">{fmtCount(row.affectedLeads)}</td>
                      <td className="text-end">
                        <Button type="button" variant="outlined" size="sm" onClick={() => setType(row.kind)}>
                          Filtra
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </Card>

          <Card variant="outlined" padding="none">
            <CardHeader title={<>
              Elenco ({fmtCount(data.count)} di {fmtCount(data.total)}){data.hasMore ? ' — restringi i filtri per vedere il resto' : ''}
              {(data.ignoredCount ?? 0) > 0 && (
                <span className="text-muted"> · {fmtCount(data.ignoredCount ?? 0)} ignorate</span>
              )}
            </>} />
            <DataTable variant="plain">
                <thead>
                  <tr><th>Lead</th><th>Tipo</th><th>Dettaglio</th><th>Stato</th><th /></tr>
                </thead>
                <tbody>
                  {data.items.length === 0 && (
                    <DataTableEmptyRow colSpan={5} title="Niente in coda." />
                  )}
                  {data.items.map(it => (
                    <tr key={`${it.leadId}-${it.kind}`}>
                      <td><Link to={`/leads/${it.leadId}`}>#{it.leadId}</Link></td>
                      <td>{LEAD_EXCEPTION_KIND_LABELS[it.kind]}</td>
                      <td className="md-typescale-body-small">
                        {it.description}
                        {it.kind === 'NumeroNonValido' && (
                          <div className="text-muted">Telefono: {it.phone || '—'}</div>
                        )}
                      </td>
                      <td><ReviewBadge review={it.review} /></td>
                      <td>
                        <ReviewActions
                          review={it.review}
                          busy={busy}
                          onTake={() => void act(() => leadExceptionsApi.review(it.leadId, { kind: it.kind, decision: 'presa_in_carico' }))}
                          onIgnore={note => void act(() => leadExceptionsApi.review(it.leadId, { kind: it.kind, decision: 'ignorata', note }))}
                          onReopen={() => void act(() => leadExceptionsApi.reopen(it.leadId, it.kind))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </Card>
        </>
      )}

      {normalizing && (
        <SourceNormalizationDialog
          onClose={() => setNormalizing(false)}
          onApplied={() => { setNormalizing(false); void load() }}
        />
      )}
    </Page>
  )
}

/** "Normalizza fonti" (Admin): i valori grezzi fuori elenco, con anteprima del conteggio e del codice scelto. */
function SourceNormalizationDialog({ onClose, onApplied }: { onClose: () => void; onApplied: () => void }) {
  const [preview, setPreview] = useState<LeadSourceNormalizationPreview | null>(null)
  const [choice, setChoice] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let alive = true
    leadExceptionsApi.sourcePreview()
      .then(p => {
        if (!alive) return
        setPreview(p)
        setChoice(Object.fromEntries(p.items.map(i => [i.raw, i.suggestedCode ?? ''])))
      })
      .catch(e => { if (alive) setError(errorText(e)) })
    return () => { alive = false }
  }, [])

  const mappings = Object.entries(choice).filter(([, code]) => code).map(([raw, code]) => ({ raw, code }))
  const affected = preview
    ? mappings.reduce((n, m) => n + (preview.items.find(i => i.raw === m.raw)?.count ?? 0), 0)
    : 0

  const apply = async () => {
    setSaving(true)
    setError(null)
    try {
      await leadExceptionsApi.sourceApply(mappings)
      onApplied()
    } catch (e) {
      setError(errorText(e))
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title="Normalizza le fonti dei lead"
      supportingText={preview ? (
        <>
          Valori di fonte fuori dall'elenco ammesso. Scegli il codice per ciascuno: l'anteprima dice quanti lead cambiano.
          Lasciando vuoto, il valore non viene toccato.
          {preview.emptyCount > 0 && ` ${fmtCount(preview.emptyCount)} lead non hanno alcuna fonte: vanno corretti uno per uno.`}
        </>
      ) : undefined}
      as="form"
      onSubmit={e => { e.preventDefault(); void apply() }}
      actions={
        <>
          <Button type="button" variant="text" dismiss onClick={onClose}>Chiudi</Button>
          <Button type="submit" variant="text" disabled={saving || mappings.length === 0}>
            Applica
          </Button>
        </>
      }
    >
      {error && <Callout tone="error">{error}</Callout>}
      {!preview && !error && <LoadingState label="Caricamento..." />}
      {preview && (
        <>
          <div className="md-dialog__bleed">
          <DataTable variant="plain">
            <thead><tr><th>Valore attuale</th><th className="text-end">Lead</th><th>Nuovo codice</th></tr></thead>
            <tbody>
              {preview.items.length === 0 && (
                <DataTableEmptyRow colSpan={3} title="Tutte le fonti sono già nell'elenco." />
              )}
              {preview.items.map(i => (
                <tr key={i.raw}>
                  <td>{i.raw}</td>
                  <td className="text-end md-num">{fmtCount(i.count)}</td>
                  <td>
                    <Select
                      label={`Codice per ${i.raw}`}
                      labelHidden
                      value={choice[i.raw] ?? ''}
                      onChange={e => setChoice(c => ({ ...c, [i.raw]: e.target.value }))}
                    >
                      <option value="">Non modificare</option>
                      {preview.allowed.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
          </div>
          <div className="md-typescale-emphasized-body-medium">Anteprima: {fmtCount(affected)} lead cambieranno fonte.</div>
        </>
      )}
    </Dialog>
  )
}

/* ── Eccezioni ───────────────────────────────────────────────────────────────────────────── */

function ExceptionsTab() {
  const [data, setData] = useState<ContractExceptionsResponse | null>(null)
  const [type, setType] = useState('')
  const [severity, setSeverity] = useState('')
  const [reviewFilter, setReviewFilter] = useState<ExceptionReviewFilter | ''>('')
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await backofficeQueueApi.exceptions({
        ...(type ? { type } : {}),
        ...(severity ? { severity } : {}),
        ...(reviewFilter ? { review: reviewFilter } : {}),
        limit: 200,
      }))
    } catch (e) {
      if (isForbidden(e)) setForbidden(true)
      else setError(errorText(e))
    } finally {
      setLoading(false)
    }
  }, [type, severity, reviewFilter])

  useEffect(() => { void load() }, [load])

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await load()
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  if (forbidden) return <Callout tone="warning" role="alert">Non sei autorizzato a vedere la coda del backoffice.</Callout>

  return (
    <Page>
      {error && <Callout tone="error">{error}</Callout>}

      <FilterBar>
        <Select id="queue-exc-type" label="Tipo" value={type} onChange={e => setType(e.target.value)}>
          <option value="">Tutti i tipi</option>
          {KIND_OPTIONS.map(k => <option key={k} value={k}>{EXCEPTION_KIND_LABELS[k]}</option>)}
        </Select>
        <Select id="queue-exc-sev" label="Gravità" value={severity} onChange={e => setSeverity(e.target.value)}>
          <option value="">Tutte</option>
          <option value="Bloccante">Bloccante</option>
          <option value="Avviso">Avviso</option>
        </Select>
        <Select
          id="queue-exc-review"
          label="Presa in carico"
          value={reviewFilter}
          onChange={e => setReviewFilter(e.target.value as ExceptionReviewFilter | '')}
        >
          {REVIEW_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>
      </FilterBar>

      {loading && !data && (
        <LoadingState label="Caricamento..." />
      )}

      {data && (
        <>
          <Card variant="outlined" padding="none">
            <CardHeader title="Per causa" />
            <DataTable variant="plain">
                <thead>
                  <tr><th>Causa</th><th>Tipo</th><th>Gravità</th><th className="text-end">Pratiche</th><th /></tr>
                </thead>
                <tbody>
                  {data.aggregate.length === 0 && (
                    <DataTableEmptyRow colSpan={5} title="Nessuna eccezione sulle pratiche aperte." />
                  )}
                  {data.aggregate.map(row => (
                    <tr key={`${row.kind}-${row.ruleCode ?? ''}-${row.label}`}>
                      <td>{row.label}</td>
                      <td>{EXCEPTION_KIND_LABELS[row.kind]}</td>
                      <td><StatusPill tone={row.severity === 'Bloccante' ? 'error' : 'warning'}>{row.severity}</StatusPill></td>
                      <td className="text-end md-num">{fmtCount(row.affectedContracts)}</td>
                      <td className="text-end">
                        <Button type="button" variant="outlined" size="sm" onClick={() => setType(row.kind)}>
                          Filtra
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </Card>

          <Card variant="outlined" padding="none">
            <CardHeader title={<>
              Elenco ({fmtCount(data.count)} di {fmtCount(data.total)}){data.hasMore ? ' — restringi i filtri per vedere il resto' : ''}
              {(data.ignoredCount ?? 0) > 0 && (
                <span className="text-muted"> · {fmtCount(data.ignoredCount ?? 0)} ignorate</span>
              )}
            </>} />
            <DataTable variant="plain">
                <thead>
                  <tr><th>Pratica</th><th>Tipo</th><th>Gravità</th><th>Dettaglio</th><th>Stato</th><th /></tr>
                </thead>
                <tbody>
                  {data.items.length === 0 && (
                    <DataTableEmptyRow colSpan={6} title="Niente in coda." />
                  )}
                  {data.items.map((it, i) => (
                    <tr key={`${it.contractId}-${it.kind}-${it.ruleCode ?? ''}-${i}`}>
                      <td><Link to={`/contracts/${it.contractId}`}>{it.referenceCode}</Link></td>
                      <td>{EXCEPTION_KIND_LABELS[it.kind]}</td>
                      <td><StatusPill tone={it.severity === 'Bloccante' ? 'error' : 'warning'}>{it.severity}</StatusPill></td>
                      <td className="md-typescale-body-small">{it.description}</td>
                      <td><ReviewBadge review={it.review} /></td>
                      <td>
                        <ReviewActions
                          review={it.review}
                          busy={busy}
                          onTake={() => void act(() => backofficeQueueApi.reviewException(it.contractId, { kind: it.kind, ruleCode: it.ruleCode, decision: 'presa_in_carico' }))}
                          onIgnore={note => void act(() => backofficeQueueApi.reviewException(it.contractId, { kind: it.kind, ruleCode: it.ruleCode, decision: 'ignorata', note }))}
                          onReopen={() => void act(() => backofficeQueueApi.reopenException(it.contractId, it.kind, it.ruleCode))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </Card>
        </>
      )}
    </Page>
  )
}

/* ── SLA ─────────────────────────────────────────────────────────────────────────────────── */

function SlaTab() {
  const [data, setData] = useState<ContractSlaResponse | null>(null)
  const [onlyOverdue, setOnlyOverdue] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [forbidden, setForbidden] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await backofficeQueueApi.sla({ onlyOverdue, limit: 200 }))
    } catch (e) {
      if (isForbidden(e)) setForbidden(true)
      else setError(errorText(e))
    } finally {
      setLoading(false)
    }
  }, [onlyOverdue])

  useEffect(() => { void load() }, [load])

  if (forbidden) return <Callout tone="warning" role="alert">Non sei autorizzato a vedere la coda del backoffice.</Callout>

  return (
    <Page>
      {error && <Callout tone="error">{error}</Callout>}

      <Callout tone="info" role="note">
        Le soglie in vigore, stato per stato, e la loro fonte (valore di serie o politica) sono nella scheda
        «Soglie SLA». Senza politiche attive valgono 48 ore (4 ore per le pratiche appena inviate al backoffice).
      </Callout>

      <Switch id="queue-sla-only" label="Solo pratiche oltre la soglia" checked={onlyOverdue} onChange={e => setOnlyOverdue(e.target.checked)} />

      {loading && !data && (
        <LoadingState label="Caricamento..." />
      )}

      {data && (
        <>
          <Card variant="outlined" padding="none">
            <CardHeader title="Per stato" />
            <DataTable variant="plain">
                <thead>
                  <tr><th>Stato</th><th className="text-end">Soglia</th><th className="text-end">Pratiche</th><th className="text-end">Nei tempi</th><th className="text-end">Oltre la soglia</th><th className="text-end">Senza storico</th></tr>
                </thead>
                <tbody>
                  {data.aggregate.length === 0 && (
                    <DataTableEmptyRow colSpan={6} title="Nessuna pratica con SLA." />
                  )}
                  {data.aggregate.map(row => (
                    <tr key={row.status}>
                      <td>{row.status}</td>
                      <td className="text-end">
                        {row.hasSla && row.thresholdHours != null ? `${fmtHours(row.thresholdHours)} h` : '—'}
                        {row.thresholdSource === 'policy' && <StatusPill tone="info" className="ms-1">politica</StatusPill>}
                      </td>
                      <td className="text-end md-num">{fmtCount(row.count)}</td>
                      <td className="text-end">{row.hasSla ? fmtCount(row.compliant) : '—'}</td>
                      <td className="text-end">{row.hasSla ? fmtCount(row.breached) : '—'}</td>
                      <td className="text-end">{row.hasSla ? fmtCount(row.noHistory) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </Card>

          <Card variant="outlined" padding="none">
            <CardHeader title={<>
              Elenco ({fmtCount(data.count)} di {fmtCount(data.total)}){data.hasMore ? ' — i più in ritardo per primi' : ''}
            </>} />
            <DataTable variant="plain">
                <thead>
                  <tr><th>Pratica</th><th>Stato</th><th>In stato dal</th><th>SLA</th><th>Priorità</th></tr>
                </thead>
                <tbody>
                  {data.items.length === 0 && (
                    <DataTableEmptyRow colSpan={5} title="Niente in coda." />
                  )}
                  {data.items.map(it => (
                    <tr key={it.contractId}>
                      <td><Link to={`/contracts/${it.contractId}`}>{it.referenceCode}</Link></td>
                      <td>{it.status}</td>
                      <td>{fmtDateTime(it.statusSince)}</td>
                      <td>
                        {it.isBreached ? (
                          <StatusPill tone="error">superato di {it.overdueLabel ?? '—'}</StatusPill>
                        ) : it.hasSla && it.elapsedHours !== null ? (
                          <StatusPill tone="success">nei tempi</StatusPill>
                        ) : (
                          <span className="text-muted md-typescale-body-small">{it.hasSla ? 'storico non disponibile' : 'senza SLA'}</span>
                        )}
                      </td>
                      <td>{PRIORITY_LABELS[it.currentPriority] ?? it.currentPriority}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </Card>
        </>
      )}
    </Page>
  )
}

/* ── Soglie SLA (F-094) ──────────────────────────────────────────────────────────────────── */

const POLICY_COLORS: Record<string, PillTone> = { Bozza: 'neutral', Attiva: 'success', Disattivata: 'neutral' }

interface PolicyForm {
  id: number | null
  contractStatus: string
  hasSla: boolean
  hours: string
}

function SlaThresholdsTab({ isAdmin }: { isAdmin: boolean }) {
  const confirm = useConfirm()
  const prompt = usePrompt()
  const [data, setData] = useState<SlaPoliciesResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [forbidden, setForbidden] = useState(false)
  const [form, setForm] = useState<PolicyForm | null>(null)
  const [previews, setPreviews] = useState<Record<number, SlaPolicyPreview>>({})

  const load = useCallback(async () => {
    try {
      setData(await backofficeQueueApi.slaPolicies())
    } catch (e) {
      if (isForbidden(e)) setForbidden(true)
      else setError(errorText(e))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function run(action: () => Promise<unknown>, success: string): Promise<boolean> {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await action()
      setNotice(success)
      await load()
      return true
    } catch (e) {
      setError(errorText(e))
      return false
    } finally {
      setBusy(false)
    }
  }

  async function preview(policy: SlaPolicy) {
    setBusy(true)
    setError(null)
    try {
      const p = await backofficeQueueApi.previewSlaPolicy(policy.id)
      setPreviews(prev => ({ ...prev, [policy.id]: p }))
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  async function activate(policy: SlaPolicy) {
    const p = previews[policy.id]
    const text = p
      ? `Attivare la soglia per ${policy.contractStatus}? ${p.newlyBreached} pratiche diventerebbero fuori soglia, ${p.noLongerBreached} rientrerebbero. Indica il motivo:`
      : `Attivare la soglia per ${policy.contractStatus} senza averne visto l'anteprima? Indica il motivo:`
    const reason = await prompt({
      title: 'Attiva la soglia',
      message: text,
      label: 'Motivo',
      required: true,
      confirmLabel: 'Attiva',
    })
    if (reason === null) return
    if (!reason.trim()) { setError('Il motivo è obbligatorio per attivare una soglia.'); return }
    await run(() => backofficeQueueApi.activateSlaPolicy(policy.id, reason.trim()), `Soglia per ${policy.contractStatus} attivata.`)
  }

  async function save() {
    if (!form) return
    const hours = form.hasSla ? Number.parseFloat(form.hours.replace(',', '.')) : null
    if (form.hasSla && (hours === null || Number.isNaN(hours))) { setError('Indica le ore di soglia.'); return }
    if (!form.contractStatus) { setError('Scegli lo stato.'); return }
    const input = { contractStatus: form.contractStatus, hasSla: form.hasSla, thresholdHours: hours }
    const ok = await run(
      () => (form.id === null ? backofficeQueueApi.createSlaPolicy(input) : backofficeQueueApi.updateSlaPolicy(form.id, input)),
      form.id === null ? 'Soglia creata in bozza.' : 'Soglia aggiornata (torna in bozza).',
    )
    if (ok) setForm(null)
  }

  // F-100: dopo "Clona" la copia (Bozza) compare nell'elenco e si apre in modifica.
  const openAfterClone = useOpenAfterClone(data?.policies ?? [], pol => setForm({
    id: pol.id, contractStatus: pol.contractStatus, hasSla: pol.hasSla,
    hours: pol.thresholdHours != null ? String(pol.thresholdHours) : '',
  }))

  if (forbidden) return <Callout tone="warning" role="alert">Non sei autorizzato a vedere le soglie SLA.</Callout>

  const editable = data?.statuses.map(s => s.status) ?? []

  return (
    <Page>
      {error && <Callout tone="error">{error}</Callout>}
      {notice && <Callout tone="success" role="status">{notice}</Callout>}

      <Callout tone="info" role="note">
        Quanto può restare una pratica in ciascuno stato prima di risultare «oltre la soglia». Senza politiche
        attive valgono i valori di serie (48 ore; 4 ore per le pratiche appena inviate al backoffice). Come
        riferimento la specifica indica 4 ore per la presa in carico e 8 ore per il caricamento: sono solo un
        suggerimento, non vengono applicate da sole. Bozza e stati finali non hanno SLA.
        {!isAdmin && ' Le soglie le modificano gli amministratori.'}
      </Callout>

      {data && (
        <Card variant="outlined" padding="none">
          <CardHeader title="Soglia in vigore per stato" />
          <DataTable variant="plain">
              <thead>
                <tr><th>Stato</th><th className="text-end">Soglia</th><th>Fonte</th><th className="text-end">Valore di serie</th></tr>
              </thead>
              <tbody>
                {data.statuses.map(s => (
                  <tr key={s.status}>
                    <td>{s.status}</td>
                    <td className="text-end">{s.hasSla && s.thresholdHours != null ? `${fmtHours(s.thresholdHours)} h` : 'Senza SLA'}</td>
                    <td>{s.thresholdSource === 'policy'
                      ? <StatusPill tone="info">politica #{s.policyId}</StatusPill>
                      : <span className="text-muted md-typescale-body-small">valore di serie</span>}</td>
                    <td className="text-end text-muted">{s.defaultThresholdHours != null ? `${fmtHours(s.defaultThresholdHours)} h` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
        </Card>
      )}

      {isAdmin && (
        <div className="mb-3">
          <Button type="button" variant="tonal" size="sm" disabled={busy || !data}
            onClick={() => setForm({ id: null, contractStatus: editable[0] ?? '', hasSla: true, hours: '' })}>
            Nuova soglia
          </Button>
        </div>
      )}

      {isAdmin && form && data && (
        <Card variant="outlined">
          <CardHeader title={form.id === null ? 'Nuova soglia' : `Modifica soglia #${form.id}`} />
          <CardContent>
            <FormGrid columns={3} className="mb-3">
              <div>
                <Select id="sla-status" label="Stato" value={form.contractStatus} onChange={e => setForm({ ...form, contractStatus: e.target.value })}>
                  {editable.map(st => <option key={st} value={st}>{st}</option>)}
                </Select>
              </div>
              <div>
                <TextField id="sla-hours" label={`Ore consentite (${fmtHours(data.minThresholdHours)}–${fmtHours(data.maxThresholdHours)})`} inputMode="decimal" disabled={!form.hasSla} value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })} />
              </div>
              <div className="d-flex align-items-end">
                <Switch id="sla-has" label="Questo stato ha uno SLA" checked={form.hasSla} onChange={e => setForm({ ...form, hasSla: e.target.checked })} />
              </div>
            </FormGrid>
            <div className="d-flex gap-2">
              <Button type="button" variant="tonal" size="sm" disabled={busy} onClick={() => void save()}>Salva</Button>
              <Button type="button" variant="text" size="sm" disabled={busy} onClick={() => setForm(null)}>Annulla</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {data && (
        <Card variant="outlined" padding="none">
          <CardHeader title="Politiche" />
          <DataTable variant="plain">
              <thead>
                <tr><th>Stato</th><th className="text-end">Soglia</th><th>Stato politica</th><th>Motivo</th>{isAdmin && <th />}</tr>
              </thead>
              <tbody>
                {data.policies.length === 0 && (
                  <DataTableEmptyRow colSpan={isAdmin ? 5 : 4} title="Nessuna politica: valgono i valori di serie." />
                )}
                {data.policies.map(pol => {
                  const p = previews[pol.id]
                  return (
                    <tr key={pol.id}>
                      <td>{pol.contractStatus}</td>
                      <td className="text-end">{pol.hasSla && pol.thresholdHours != null ? `${fmtHours(pol.thresholdHours)} h` : 'Senza SLA'}</td>
                      <td><StatusPill tone={POLICY_COLORS[pol.status] ?? 'neutral'}>{pol.status}</StatusPill></td>
                      <td className="md-typescale-body-small">
                        {pol.reason ?? '—'}
                        {p && (
                          <div className="mt-1 text-primary">
                            Anteprima su {fmtCount(p.examined)} pratiche in {p.contractStatus}: oggi fuori soglia {fmtCount(p.breachedNow)},
                            con questa soglia {fmtCount(p.breachedWithPolicy)} ({fmtCount(p.newlyBreached)} nuove, {fmtCount(p.noLongerBreached)} rientrate).{' '}
                            {p.newlyBreachedSampleIds.slice(0, 10).map(id => (
                              <Link key={id} className="me-1" to={`/contracts/${id}`}>#{id}</Link>
                            ))}
                            {p.newlyBreached > 10 && <span className="text-muted">… (prime {p.sampleLimit} nell'anteprima)</span>}
                          </div>
                        )}
                      </td>
                      {isAdmin && (
                        <td className="text-end text-nowrap">
                          <CloneConfigurationButton
                            entityLabel="soglia SLA"
                            url={`/api/contracts/sla-policies/${pol.id}/clone`}
                            defaultName={`Soglia ${pol.contractStatus}`}
                            extraFields={[{ key: 'contractStatus', label: 'Stato di destinazione (vuoto = lo stesso)', placeholder: 'es. DaVerificare' }]}
                            note="Il nome non è usato (la soglia è per stato). Si copiano ore e flag SLA; la copia nasce in bozza, senza motivo né attivazione."
                            onCloned={async id => { await load(); setNotice(CLONE_NOTICE); openAfterClone(id) }}
                          />
                          <Button type="button" variant="outlined" size="sm" className="me-1" disabled={busy} onClick={() => void preview(pol)}>Anteprima</Button>
                          {pol.status !== 'Attiva' && (
                            <>
                              <Button type="button" variant="outlined" size="sm" className="me-1" disabled={busy}
                                onClick={() => setForm({ id: pol.id, contractStatus: pol.contractStatus, hasSla: pol.hasSla, hours: pol.thresholdHours != null ? String(pol.thresholdHours) : '' })}>
                                Modifica
                              </Button>
                              <Button type="button" variant="tonal" size="sm" className="me-1" disabled={busy} onClick={() => void activate(pol)}>Attiva</Button>
                              <Button type="button" variant="outlined" size="sm" disabled={busy}
                                onClick={() => { void (async () => { if (await confirm({ title: 'Eliminare la soglia?', message: `Eliminare la soglia per ${pol.contractStatus}?`, confirmLabel: 'Elimina', destructive: true })) void run(() => backofficeQueueApi.deleteSlaPolicy(pol.id), 'Soglia eliminata.') })() }}>
                                Elimina
                              </Button>
                            </>
                          )}
                          {pol.status === 'Attiva' && (
                            <Button type="button" variant="tonal" size="sm" disabled={busy}
                              onClick={() => void run(() => backofficeQueueApi.deactivateSlaPolicy(pol.id), `Soglia per ${pol.contractStatus} disattivata.`)}>
                              Disattiva
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </DataTable>
        </Card>
      )}
    </Page>
  )
}

/* ── Regole di priorità ──────────────────────────────────────────────────────────────────── */

const FIELD_LABELS: Record<string, string> = {
  status: 'Stato',
  isBreached: 'SLA superato',
  hoursOverdue: 'Ore di ritardo',
  readiness: 'Completezza',
  exceptionKinds: 'Eccezioni',
  operatorId: 'Operatore (id)',
  productId: 'Prodotto (id)',
  departmentId: 'Settore (id)',
  customerSegment: 'Segmento cliente',
  agentId: 'Agente (id)',
}

const OPERATOR_LABELS: Record<string, string> = {
  eq: 'è',
  ne: 'non è',
  gt: 'maggiore di',
  lt: 'minore di',
  contains: 'contiene',
}

const VALUE_HINTS: Record<string, string[]> = {
  isBreached: ['true', 'false'],
  readiness: ['red', 'yellow', 'green'],
  exceptionKinds: KIND_OPTIONS,
  status: ['Bozza', 'InviataAlBackoffice', 'DaVerificare', 'IntegrazioneRichiesta', 'Verificata', 'AssegnataALotto'],
}

const STATUS_COLORS: Record<string, PillTone> = {
  Bozza: 'neutral', Attiva: 'success', Disattivata: 'neutral',
  Test: 'info', Simulazione: 'info', Approvazione: 'warning', Pubblicata: 'primary', Archiviata: 'neutral',
}

function describeConditions(conditions: PriorityCondition[]): string {
  return conditions
    .map(c => `${FIELD_LABELS[c.field] ?? c.field} ${OPERATOR_LABELS[c.operator] ?? c.operator} ${c.value}`)
    .join(' e ')
}

interface RuleForm {
  id: number | null
  name: string
  order: string
  targetPriority: PriorityValue
  conditions: PriorityCondition[]
}

const EMPTY_FORM: RuleForm = {
  id: null,
  name: '',
  order: '100',
  targetPriority: 'urgent',
  conditions: [{ field: 'isBreached', operator: 'eq', value: 'true' }],
}

function RulesTab() {
  const confirm = useConfirm()
  const [data, setData] = useState<PriorityRulesResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState<RuleForm | null>(null)
  const [previews, setPreviews] = useState<Record<number, PriorityRulePreview>>({})

  const load = useCallback(async () => {
    try {
      setData(await backofficeQueueApi.rules())
    } catch (e) {
      setError(errorText(e))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await action()
      setNotice(success)
      await load()
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  async function preview(rule: PriorityRule) {
    setBusy(true)
    setError(null)
    try {
      const p = await backofficeQueueApi.previewRule(rule.id)
      setPreviews(prev => ({ ...prev, [rule.id]: p }))
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  async function activate(rule: PriorityRule) {
    const p = previews[rule.id]
    const text = p
      ? `Attivare la regola "${rule.name}"? Cambierebbe la priorità di ${p.wouldChange} pratiche.`
      : `Attivare la regola "${rule.name}" senza averne visto l'anteprima?`
    if (!(await confirm({ title: 'Attivare la regola?', message: text, confirmLabel: 'Attiva' }))) return
    await run(() => backofficeQueueApi.activateRule(rule.id), `Regola "${rule.name}" attivata.`)
  }

  async function save() {
    if (!form) return
    const input: PriorityRuleInput = {
      name: form.name.trim(),
      order: Number.parseInt(form.order, 10),
      targetPriority: form.targetPriority,
      conditions: form.conditions,
    }
    if (!input.name) { setError('Il nome è obbligatorio.'); return }
    if (Number.isNaN(input.order)) { setError('L\'ordine deve essere un numero.'); return }
    await run(
      () => (form.id === null ? backofficeQueueApi.createRule(input) : backofficeQueueApi.updateRule(form.id, input)),
      form.id === null ? 'Regola creata in bozza.' : 'Regola aggiornata (torna in bozza).',
    )
    setForm(null)
  }

  // F-100: dopo "Clona" la copia (Bozza) compare nell'elenco e si apre in modifica.
  const openAfterClone = useOpenAfterClone(data?.rules ?? [], rule => setForm({
    id: rule.id, name: rule.name, order: String(rule.order), targetPriority: rule.targetPriority,
    conditions: rule.conditions.map(c => ({ ...c })),
  }))

  function updateCondition(index: number, patch: Partial<PriorityCondition>) {
    setForm(f => f && { ...f, conditions: f.conditions.map((c, i) => (i === index ? { ...c, ...patch } : c)) })
  }

  return (
    <Page>
      {error && <Callout tone="error">{error}</Callout>}
      {notice && <Callout tone="success" role="status">{notice}</Callout>}

      <Callout tone="info" role="note">
        Una regola nasce in bozza e non fa nulla finché non la attivi. L'applicazione automatica sulle pratiche la
        esegue il server ogni 15 minuti, solo se è acceso (<code>Contracts:PriorityRules:Enabled</code>). Una
        priorità impostata a mano non viene mai cambiata da una regola; vince la prima regola attiva per ordine.
        Se una regola smette di applicarsi, le priorità che aveva impostato tornano a Normale.
      </Callout>

      <div className="mb-3">
        <Button type="button" variant="tonal" size="sm" disabled={busy} onClick={() => setForm({ ...EMPTY_FORM, conditions: EMPTY_FORM.conditions.map(c => ({ ...c })) })}>
          Nuova regola
        </Button>
      </div>

      {form && data && (
        <Card variant="outlined">
          <CardHeader title={form.id === null ? 'Nuova regola' : `Modifica regola #${form.id}`} />
          <CardContent>
            <FormGrid columns={4} className="mb-3">
              <div className="md-span-2">
                <TextField id="rule-name" label="Nome" value={form.name} maxLength={100} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Select id="rule-target" label="Imposta la priorità a" value={form.targetPriority} onChange={e => setForm({ ...form, targetPriority: e.target.value as PriorityValue })}>
                  {data.priorities.map(p => <option key={p} value={p}>{PRIORITY_LABELS[p] ?? p}</option>)}
                </Select>
              </div>
              <div>
                <TextField id="rule-order" label="Ordine (il più basso vince)" inputMode="numeric" value={form.order} onChange={e => setForm({ ...form, order: e.target.value })} />
              </div>
            </FormGrid>

            <div className="md-typescale-emphasized-body-small mb-1">Quando (tutte le condizioni)</div>
            {form.conditions.map((c, i) => (
              <FormGrid columns={4} key={i} className="mb-2">
                <div>
                  <Select label="Attributo" labelHidden value={c.field} onChange={e => updateCondition(i, { field: e.target.value, value: '' })}>
                    {data.attributes.map(a => <option key={a} value={a}>{FIELD_LABELS[a] ?? a}</option>)}
                  </Select>
                </div>
                <div>
                  <Select label="Operatore" labelHidden value={c.operator} onChange={e => updateCondition(i, { operator: e.target.value })}>
                    {data.operators.map(o => <option key={o} value={o}>{OPERATOR_LABELS[o] ?? o}</option>)}
                  </Select>
                </div>
                <div>
                  <TextField label="Valore" labelHidden list={`hints-${i}`} value={c.value} maxLength={100} onChange={e => updateCondition(i, { value: e.target.value })} />
                  <datalist id={`hints-${i}`}>
                    {(VALUE_HINTS[c.field] ?? []).map(v => <option key={v} value={v} />)}
                  </datalist>
                </div>
                <div>
                  <Button type="button" variant="outlined" size="sm" aria-label="Togli condizione" disabled={form.conditions.length <= 1} onClick={() => setForm({ ...form, conditions: form.conditions.filter((_, j) => j !== i) })}>
                    ×
                  </Button>
                </div>
              </FormGrid>
            ))}
            <Button type="button" variant="outlined" size="sm" className="mb-3" disabled={form.conditions.length >= 10} onClick={() => setForm({ ...form, conditions: [...form.conditions, { field: 'isBreached', operator: 'eq', value: 'true' }] })}>
              Aggiungi condizione
            </Button>

            <div className="d-flex gap-2">
              <Button type="button" variant="tonal" size="sm" disabled={busy} onClick={() => void save()}>Salva</Button>
              <Button type="button" variant="text" size="sm" disabled={busy} onClick={() => setForm(null)}>Annulla</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card variant="outlined" padding="none">
        <CardHeader title="Regole" />
        <DataTable variant="plain">
            <thead>
              <tr><th>Ordine</th><th>Nome</th><th>Condizioni</th><th>Priorità</th><th>Stato</th><th /></tr>
            </thead>
            <tbody>
              {data && data.rules.length === 0 && (
                <DataTableEmptyRow colSpan={6} title="Nessuna regola." />
              )}
              {data?.rules.map(rule => {
                const p = previews[rule.id]
                return (
                  <tr key={rule.id}>
                    <td>{rule.order}</td>
                    <td>{rule.name}</td>
                    <td className="md-typescale-body-small">
                      {describeConditions(rule.conditions)}
                      {p && (
                        <div className="mt-1 text-primary">
                          Anteprima: {fmtCount(p.matching)} pratiche corrispondono, {fmtCount(p.wouldChange)} cambierebbero priorità
                          {Object.entries(p.byTargetPriority).length > 0 && (
                            <> ({Object.entries(p.byTargetPriority).map(([k, v]) => `${PRIORITY_LABELS[k] ?? k}: ${v}`).join(', ')})</>
                          )}
                          {p.skippedManual > 0 && <>; {fmtCount(p.skippedManual)} con priorità manuale non toccate</>}
                          .{' '}
                          {p.sampleContractIds.slice(0, 10).map(id => (
                            <Link key={id} className="me-1" to={`/contracts/${id}`}>#{id}</Link>
                          ))}
                          {p.wouldChange > 10 && <span className="text-muted">… (prime {p.sampleLimit} nell'anteprima)</span>}
                        </div>
                      )}
                    </td>
                    <td>{PRIORITY_LABELS[rule.targetPriority] ?? rule.targetPriority}</td>
                    <td><StatusPill tone={STATUS_COLORS[rule.status] ?? 'neutral'}>{rule.status}</StatusPill></td>
                    <td className="text-end text-nowrap">
                      <CloneConfigurationButton
                        entityLabel="regola di priorità"
                        url={`/api/contracts/priority-rules/${rule.id}/clone`}
                        defaultName={rule.name}
                        note="Si copiano condizioni, priorità di destinazione e ordine. La copia nasce in bozza; l'originale non cambia."
                        onCloned={async id => { await load(); setNotice(CLONE_NOTICE); openAfterClone(id) }}
                      />
                      <Button type="button" variant="outlined" size="sm" className="me-1" disabled={busy} onClick={() => void preview(rule)}>Anteprima</Button>
                      {rule.status !== 'Attiva' && (
                        <>
                          <Button type="button" variant="outlined" size="sm" className="me-1" disabled={busy}
                            onClick={() => setForm({ id: rule.id, name: rule.name, order: String(rule.order), targetPriority: rule.targetPriority, conditions: rule.conditions.map(c => ({ ...c })) })}>
                            Modifica
                          </Button>
                          <Button type="button" variant="tonal" size="sm" className="me-1" disabled={busy} onClick={() => void activate(rule)}>Attiva</Button>
                          <Button type="button" variant="outlined" size="sm" disabled={busy}
                            onClick={() => { void (async () => { if (await confirm({ title: 'Eliminare la regola?', message: `Eliminare la regola "${rule.name}"?`, confirmLabel: 'Elimina', destructive: true })) void run(() => backofficeQueueApi.deleteRule(rule.id), 'Regola eliminata.') })() }}>
                            Elimina
                          </Button>
                        </>
                      )}
                      {rule.status === 'Attiva' && (
                        <Button type="button" variant="tonal" size="sm" disabled={busy}
                          onClick={() => void run(() => backofficeQueueApi.deactivateRule(rule.id), `Regola "${rule.name}" disattivata.`)}>
                          Disattiva
                        </Button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </DataTable>
      </Card>

      <RuleLifecyclePanel family="priority" onChanged={() => void load()} />
    </Page>
  )
}
